import { Route, Routes } from 'react-router';
import Layout from './Layout';
import ProtectedRoute from "../../common/ProtectedRoute";
import { NotFound } from "../../common/NotFound";
import { Navigate } from 'react-router-dom';
import { InvoiceList } from '../trading-system/invoices/InvoiceList';
import { PackingListList } from '../trading-system/packing-lists/PackingListList';
import { PurchaseOrderList } from '../trading-system/purchase-orders/PurchaseOrderList';
import { QuotationList } from '../trading-system/quotations/QuotationList';
import Form from '../../sandbox/Sandbox';
import { DropDownComboOption } from './GenericFilter';
import React from 'react';
import { useConstructor } from '../../common/UseConstructor';
import axios, { AxiosResponse } from 'axios';
import { CustomerCombo } from '../trading-system/customers/CustomerAccessories';
import { EndUserCombo } from '../trading-system/end-users/EndUserAccessories';
import { RegionCombo } from '../trading-system/regions/RegionAccessories';
import { StaffCombo } from '../trading-system/staffs/StaffAccessories';
import { DeliveryMethodComboItem } from '../trading-system/delivery-methods/DeliveryMethodAccessories';
import { PaymentTermComboItem } from '../trading-system/payment-terms/PaymentTermAccessories';
import { CurrencyComboItem } from '../trading-system/exchange-rates/ExchangeRateAccessories';
import CircularProgress from '@mui/material/CircularProgress';
import { getDefaultPage } from '../../services/AuthService';
import { BankAccountList } from '../trading-system/banks/BankAccountList';
import { BranchList } from '../trading-system/branches/BranchList';
import { StatementList } from '../trading-system/account/daily/StatementList';
import { StatementRecordList } from '../trading-system/account/daily/StatementRecordList';
import { CustomerList } from '../trading-system/customers/CustomerList';
import { ProductManagement } from '../trading-system/products/ProductManagement';
import { ProductPackageManagement } from '../trading-system/products/ProductPackageManagement';
import { EndUserManagement } from '../trading-system/end-users/EndUserManagement';
import { DeliveryMethodManagement } from '../trading-system/delivery-methods/DeliveryMethodManagement';
import { PaymentTermManagement } from '../trading-system/payment-terms/PaymentTermManagement';
import { ExchangeRateManagement } from '../trading-system/exchange-rates/ExchangeRateManagement';
import { CustomerEnvelops } from '../trading-system/customers/CustomerEnvelops';
import { ManufacturerList } from '../trading-system/manufacturers/ManufacturerList';
import { BoxSizeManagement } from '../trading-system/boxSizes/BoxSizeManagement';
import { StaffList } from '../trading-system/staffs/StaffList';
import { StaffPermissionManagement } from '../trading-system/staffs/StaffPermissionManagement';
import { PermissionManagement } from '../trading-system/staffs/PermissionManagement';
import { PurchaseOrderHistory } from '../trading-system/purchase-orders/PurchaseOrderHistory';
import { QuotationHistory } from '../trading-system/quotations/QuotationHistory';
import { InvoiceHistory } from '../trading-system/invoices/InvoiceHistory';
import { PurchaseOrderFilingList } from '../trading-system/purchase-orders/PurchaseOrderFilingList';
import { InvoiceFiling } from '../trading-system/invoices/InvoiceFiling';
import { PurchaseOrderEnquiry } from '../trading-system/purchase-orders/PurchaseOrderEnquiry';
import { PurchaseOrderRanking } from '../trading-system/purchase-orders/PurchaseOrderRanking';
import { PurchaseOrderAnalysis } from '../trading-system/purchase-orders/PurchaseOrderAnalysis';
import { PurchaseOrderAnalysisSalesman } from '../trading-system/purchase-orders/PurchaseOrderAnalysisSalesman';
import { PurchaseOrderSalesSummary } from '../trading-system/purchase-orders/PurchaseOrderSalesSummary';

export interface SearchOptions {
	manufacturers?: DropDownComboOption[],
	customers: DropDownComboOption[],
    endUsers: EndUserCombo[],
    customerPoNo?: string,
    regions: DropDownComboOption[],
	staffs: DropDownComboOption[],
    deliveryMethods: DropDownComboOption[],
    paymentTerms: DropDownComboOption[],
    currencies: DropDownComboOption[],
    isSample?: boolean
}

export interface CommonProps {
    manufacturers?: DropDownComboOption[];
    customers: DropDownComboOption[];
    endUsers: EndUserCombo[];
    customerPoNo?: string;
    regions: DropDownComboOption[];
    sales: DropDownComboOption[];
    deliveryMethods: DropDownComboOption[];
    paymentTerms: DropDownComboOption[];
    currencies: DropDownComboOption[];
    isSample?: boolean;
    isOutstanding?: boolean; 
    onClose: Function;
}

export function MainContents() {
	const [loading, setLoading] = React.useState(true);
	const [hasError, setHasError] = React.useState(false);

    const searchOptions = React.useRef<SearchOptions>({
        manufacturers: [],
        customers: [],
        endUsers: [],
        customerPoNo: "",
        regions: [],
        staffs: [],
        deliveryMethods: [],
        paymentTerms: [],
        currencies: [],
        isSample: false
    });
    
	useConstructor(() => Initialise())
    function Initialise() {
        setLoading(true);

        axios.all([
            axios.get<any[]>(`Api/V1/Customer/Combo`, {
                headers: {
                    'Content-Encoding': 'gzip'
                }
            }),
            axios.get<any[]>(`Api/V1/EndUser/Combo`, {
                headers: {
                    'Content-Encoding': 'gzip'
                }
            }),
            axios.get<any[]>(`Api/V1/Region/Combo`, {
                headers: {
                    'Content-Encoding': 'gzip'
                }
            }),
            axios.get<any[]>(`Api/V1/Staff`, {
                headers: {
                    'Content-Encoding': 'gzip'
                }
            }),
            axios.get<any[]>(`Api/V1/DeliveryMethod`, {
                headers: {
                    'Content-Encoding': 'gzip'
                }
            }),
            axios.get<any[]>(`Api/V1/PaymentTerm`, {
                headers: {
                    'Content-Encoding': 'gzip'
                }
            }),
            axios.get<any[]>(`Api/V1/ExchangeRate`, {
                headers: {
                    'Content-Encoding': 'gzip'
                }
            })
		]).then(axios.spread((...responses) => {
            const customerComboResult: AxiosResponse<CustomerCombo[]> = responses[0];
            let loadedCustomers: DropDownComboOption[] = [];
            customerComboResult.data.map(c => loadedCustomers.push({ name: c.companyName, value: c.customerId.toString() }));

            const endUserComboResult: AxiosResponse<EndUserCombo[]> = responses[1];

            const regionComboResult: AxiosResponse<RegionCombo[]> = responses[2];
            let loadedRegions: DropDownComboOption[] = [];
            regionComboResult.data.map(r => loadedRegions.push({ name: r.regionName, value: r.regionName }));

            const staffComboResult: AxiosResponse<StaffCombo[]> = responses[3];
            let loadedStaffs: DropDownComboOption[] = [];
            staffComboResult.data.map(s => loadedStaffs.push({ name: s.staffName, value: s.staffId.toString() }));

            const deliveryMethodComboResult: AxiosResponse<DeliveryMethodComboItem[]> = responses[4];
            let loadedDeliveryMethods: DropDownComboOption[] = [];
            deliveryMethodComboResult.data.map(s => loadedDeliveryMethods.push({ name: s.deliveryMethodDescription, value: s.deliveryMethodDescription }));

            const paymentTermComboResult: AxiosResponse<PaymentTermComboItem[]> = responses[5];
            let loadedPaymentTerms: DropDownComboOption[] = [];
            paymentTermComboResult.data.map(s => loadedPaymentTerms.push({ name: s.paymentTermDescription, value: s.paymentTermDescription }));

            const currencyComboResult: AxiosResponse<CurrencyComboItem[]> = responses[6];
            let loadedCurrencies: DropDownComboOption[] = [];
            currencyComboResult.data.map(s => loadedCurrencies.push({ name: s.currencyDescription, value: s.exRate.toString() }));

            searchOptions.current.customers = loadedCustomers;
            searchOptions.current.endUsers = endUserComboResult.data;
            searchOptions.current.regions = loadedRegions;
			searchOptions.current.staffs = loadedStaffs;
            searchOptions.current.deliveryMethods = loadedDeliveryMethods;
            searchOptions.current.paymentTerms = loadedPaymentTerms;
            searchOptions.current.currencies = loadedCurrencies;
            
            setLoading(false);
        })).catch((error) => {
            setHasError(true);
            setLoading(false);
        })
    }

    if (!loading)
        return (
            <div>
                <Layout>
                    <Routes>
                        <Route path='quotation' element={<ProtectedRoute 
                                                            outlet={<QuotationList 
                                                                        customers={searchOptions.current.customers} 
                                                                        endUsers={searchOptions.current.endUsers}
                                                                        regions={searchOptions.current.regions}
                                                                        sales={searchOptions.current.staffs}
                                                                        deliveryMethods={searchOptions.current.deliveryMethods}
                                                                        paymentTerms={searchOptions.current.paymentTerms}
                                                                        currencies={searchOptions.current.currencies}
                                                                        onClose={()=>{}}
                                                                    />} authenticationPath={'/Login'} />} />
                        <Route path='purchaseOrder' element={<ProtectedRoute outlet={<PurchaseOrderList 
                                                                        customers={searchOptions.current.customers} 
                                                                        endUsers={searchOptions.current.endUsers}
                                                                        regions={searchOptions.current.regions}
                                                                        sales={searchOptions.current.staffs}
                                                                        deliveryMethods={searchOptions.current.deliveryMethods}
                                                                        paymentTerms={searchOptions.current.paymentTerms}
                                                                        currencies={searchOptions.current.currencies}
                                                                        onClose={()=>{}}
                                                                    />} authenticationPath={'/Login'} />} />
                        <Route path='invoice' element={<ProtectedRoute outlet={<InvoiceList 
                                                                        customers={searchOptions.current.customers} 
                                                                        endUsers={searchOptions.current.endUsers}
                                                                        regions={searchOptions.current.regions}
                                                                        sales={searchOptions.current.staffs}
                                                                        deliveryMethods={[]}
                                                                        paymentTerms={searchOptions.current.paymentTerms}
                                                                        currencies={searchOptions.current.currencies}
                                                                        onClose={()=>{}}
                                                                    />} authenticationPath={'/Login'} />} />
                        <Route path='packingList' element={<ProtectedRoute outlet={<PackingListList 
                                                                        customers={searchOptions.current.customers}
                                                                        endUsers={[]}
                                                                        regions={searchOptions.current.regions}
                                                                        sales={[]}
                                                                        deliveryMethods={[]}
                                                                        paymentTerms={[]}
                                                                        currencies={[]}
                                                                        onClose={()=>{}}                                                                    
                                                                    />} authenticationPath={'/Login'} />} />
                        <Route path='bankAccounts' element={<ProtectedRoute outlet={<BankAccountList 
                                                                        customers={searchOptions.current.customers}
                                                                        endUsers={[]}
                                                                        regions={searchOptions.current.regions}
                                                                        sales={[]}
                                                                        deliveryMethods={[]}
                                                                        paymentTerms={[]}
                                                                        currencies={[]}
                                                                        onClose={()=>{}}                                                                    
                                                                    />} authenticationPath={'/Login'} />} />
                        <Route path='branches' element={<ProtectedRoute outlet={<BranchList 
                                                                        customers={[]}
                                                                        endUsers={[]}
                                                                        regions={searchOptions.current.regions}
                                                                        sales={[]}
                                                                        deliveryMethods={[]}
                                                                        paymentTerms={[]}
                                                                        currencies={[]}
                                                                        onClose={()=>{}}                                                                    
                                                                    />} authenticationPath={'/Login'} />} />
                        <Route path='statements' element={<ProtectedRoute outlet={<StatementList 
                                                                        customers={searchOptions.current.customers}
                                                                        endUsers={[]}
                                                                        regions={searchOptions.current.regions}
                                                                        sales={[]}
                                                                        deliveryMethods={[]}
                                                                        paymentTerms={searchOptions.current.paymentTerms}
                                                                        currencies={searchOptions.current.currencies}
                                                                        onClose={()=>{}}                                                                    
                                                                    />} authenticationPath={'/Login'} />} />
                        <Route path='statementRecords' element={<ProtectedRoute outlet={<StatementRecordList 
                                                                        customers={searchOptions.current.customers}
                                                                        endUsers={[]}
                                                                        regions={searchOptions.current.regions}
                                                                        sales={[]}
                                                                        deliveryMethods={[]}
                                                                        paymentTerms={searchOptions.current.paymentTerms}
                                                                        currencies={searchOptions.current.currencies}
                                                                        onClose={()=>{}}                                                                    
                                                                    />} authenticationPath={'/Login'} />} />
                        <Route path='exchangeRates' element={<ProtectedRoute outlet={<ExchangeRateManagement />} authenticationPath={'/Login'} />} />
                        <Route path='customers' element={<ProtectedRoute outlet={<CustomerList 
                                                                        customers={[]}
                                                                        endUsers={[]}
                                                                        regions={[]}
                                                                        sales={[]}
                                                                        deliveryMethods={[]}
                                                                        paymentTerms={searchOptions.current.paymentTerms}
                                                                        currencies={[]}
                                                                        onClose={()=>{}}                                                                    
                                                                    />} authenticationPath={'/Login'} />} />
                        <Route path='products' element={<ProtectedRoute outlet={<ProductManagement />} authenticationPath={'/Login'} />} />
                        <Route path='productPackages' element={<ProtectedRoute outlet={<ProductPackageManagement />} authenticationPath={'/Login'} />} />
                        <Route path='endUsers' element={<ProtectedRoute outlet={<EndUserManagement 
                                                                        customers={searchOptions.current.customers}
                                                                        endUsers={[]}
                                                                        regions={searchOptions.current.regions}
                                                                        sales={[]}
                                                                        deliveryMethods={[]}
                                                                        paymentTerms={[]}
                                                                        currencies={[]}
                                                                        onClose={()=>{}}                                                                    
                                                                    />} authenticationPath={'/Login'} />} />
                        <Route path='deliveryMethods' element={<ProtectedRoute outlet={<DeliveryMethodManagement />} authenticationPath={'/Login'} />} />
                        <Route path='paymentTerms' element={<ProtectedRoute outlet={<PaymentTermManagement />} authenticationPath={'/Login'} />} />
                        <Route path='customerEnvelops' element={<ProtectedRoute outlet={<CustomerEnvelops 
                                                                        customers={[]}
                                                                        endUsers={[]}
                                                                        regions={[]}
                                                                        sales={[]}
                                                                        deliveryMethods={[]}
                                                                        paymentTerms={[]}
                                                                        currencies={[]}
                                                                        onClose={()=>{}}                                                                    
                                                                    />} authenticationPath={'/Login'} />} />
                        <Route path='manufacturers' element={<ProtectedRoute outlet={<ManufacturerList 
                                                                        customers={[]}
                                                                        endUsers={[]}
                                                                        regions={[]}
                                                                        sales={[]}
                                                                        deliveryMethods={[]}
                                                                        paymentTerms={searchOptions.current.paymentTerms}
                                                                        currencies={[]}
                                                                        onClose={()=>{}}                                                                    
                                                                    />} authenticationPath={'/Login'} />} />
                        <Route path='boxSizes' element={<ProtectedRoute outlet={<BoxSizeManagement />} authenticationPath={'/Login'} />} />
                        <Route path='staffs' element={<ProtectedRoute outlet={<StaffList 
                                                                        customers={[]}
                                                                        endUsers={[]}
                                                                        regions={searchOptions.current.regions}
                                                                        sales={[]}
                                                                        deliveryMethods={[]}
                                                                        paymentTerms={[]}
                                                                        currencies={[]}
                                                                        onClose={()=>{}}                                                                    
                                                                    />} authenticationPath={'/Login'} />} />
                        <Route path='staffPermissions' element={<ProtectedRoute outlet={<StaffPermissionManagement />} authenticationPath={'/Login'} />} />
                        <Route path='permissions' element={<ProtectedRoute outlet={<PermissionManagement />} authenticationPath={'/Login'} />} />
                        <Route path='purchaseorderHistory' element={<ProtectedRoute outlet={<PurchaseOrderHistory 
                                                                        customers={searchOptions.current.customers} 
                                                                        endUsers={searchOptions.current.endUsers}
                                                                        regions={searchOptions.current.regions}
                                                                        sales={searchOptions.current.staffs}
                                                                        deliveryMethods={searchOptions.current.deliveryMethods}
                                                                        paymentTerms={searchOptions.current.paymentTerms}
                                                                        currencies={searchOptions.current.currencies}
                                                                        onClose={()=>{}}
                                                                    />} authenticationPath={'/Login'} />} />
                        <Route path='quotationHistory' element={<ProtectedRoute outlet={<QuotationHistory 
                                                                        customers={searchOptions.current.customers} 
                                                                        endUsers={searchOptions.current.endUsers}
                                                                        regions={searchOptions.current.regions}
                                                                        sales={searchOptions.current.staffs}
                                                                        deliveryMethods={searchOptions.current.deliveryMethods}
                                                                        paymentTerms={searchOptions.current.paymentTerms}
                                                                        currencies={searchOptions.current.currencies}
                                                                        onClose={()=>{}}
                                                                    />} authenticationPath={'/Login'} />} />
                        <Route path='invoiceHistory' element={<ProtectedRoute outlet={<InvoiceHistory 
                                                                        customers={searchOptions.current.customers} 
                                                                        endUsers={searchOptions.current.endUsers}
                                                                        regions={searchOptions.current.regions}
                                                                        sales={searchOptions.current.staffs}
                                                                        deliveryMethods={searchOptions.current.deliveryMethods}
                                                                        paymentTerms={searchOptions.current.paymentTerms}
                                                                        currencies={searchOptions.current.currencies}
                                                                        onClose={()=>{}}
                                                                    />} authenticationPath={'/Login'} />} />
                        <Route path='purchaseorderFilingRecord' element={<ProtectedRoute outlet={<PurchaseOrderFilingList 
                                                                        customers={[]} 
                                                                        endUsers={[]}
                                                                        regions={searchOptions.current.regions}
                                                                        sales={[]}
                                                                        deliveryMethods={[]}
                                                                        paymentTerms={[]}
                                                                        currencies={[]}
                                                                        onClose={()=>{}}
                                                                    />} authenticationPath={'/Login'} />} />
                        <Route path='invoiceFilingRecord' element={<ProtectedRoute outlet={<InvoiceFiling 
                                                                        customers={[]} 
                                                                        endUsers={[]}
                                                                        regions={searchOptions.current.regions}
                                                                        sales={[]}
                                                                        deliveryMethods={[]}
                                                                        paymentTerms={[]}
                                                                        currencies={[]}
                                                                        onClose={()=>{}}
                                                                    />} authenticationPath={'/Login'} />} />
                        <Route path='purchaseOrderEnquiry' element={<ProtectedRoute outlet={<PurchaseOrderEnquiry 
                                                                        customers={[]} 
                                                                        endUsers={[]}
                                                                        regions={searchOptions.current.regions}
                                                                        sales={[]}
                                                                        deliveryMethods={[]}
                                                                        paymentTerms={[]}
                                                                        currencies={[]}
                                                                        onClose={()=>{}}
                                                                    />} authenticationPath={'/Login'} />} />
                        <Route path='purchaseOrderRanking' element={<ProtectedRoute outlet={<PurchaseOrderRanking 
                                                                        customers={[]} 
                                                                        endUsers={[]}
                                                                        regions={searchOptions.current.regions}
                                                                        sales={[]}
                                                                        deliveryMethods={[]}
                                                                        paymentTerms={[]}
                                                                        currencies={[]}
                                                                        onClose={()=>{}}
                                                                    />} authenticationPath={'/Login'} />} />
                        <Route path='purchaseOrderAnalysis' element={<ProtectedRoute outlet={<PurchaseOrderAnalysis 
                                                                        customers={[]} 
                                                                        endUsers={[]}
                                                                        regions={searchOptions.current.regions}
                                                                        sales={[]}
                                                                        deliveryMethods={[]}
                                                                        paymentTerms={[]}
                                                                        currencies={[]}
                                                                        onClose={()=>{}}
                                                                    />} authenticationPath={'/Login'} />} />
                        <Route path='purchaseOrderAnalysisSalesman' element={<ProtectedRoute outlet={<PurchaseOrderAnalysisSalesman 
                                                                        customers={[]} 
                                                                        endUsers={[]}
                                                                        regions={searchOptions.current.regions}
                                                                        sales={[]}
                                                                        deliveryMethods={[]}
                                                                        paymentTerms={[]}
                                                                        currencies={[]}
                                                                        onClose={()=>{}}
                                                                    />} authenticationPath={'/Login'} />} />
                        <Route path='purchaseOrderSalesSummary' element={<ProtectedRoute outlet={<PurchaseOrderSalesSummary 
                                                                        customers={[]} 
                                                                        endUsers={[]}
                                                                        regions={searchOptions.current.regions}
                                                                        sales={searchOptions.current.staffs}
                                                                        deliveryMethods={[]}
                                                                        paymentTerms={[]}
                                                                        currencies={[]}
                                                                        onClose={()=>{}}
                                                                    />} authenticationPath={'/Login'} />} />
                        <Route path='/' element={<Navigate to={'/app/' + getDefaultPage()} replace />} />
                        <Route path="*" element={NotFound}/>
                        <Route path='sandbox' element={<ProtectedRoute outlet={<Form />} authenticationPath={'/Login'} />} />
                    </Routes>
                </Layout>
            </div>
        );
    else
        return(
            <div style={{ textAlign: 'center' }}>
                <CircularProgress color="primary" />
            </div>
        )    
}